import Icon from '@ant-design/icons'
import { Empty } from 'antd'
import { theme } from 'constants/theme'
import Highcharts from 'highcharts'
import Accessibility from 'highcharts/modules/accessibility'
import Heatmap from 'highcharts/modules/heatmap'
import { NoDataFolder } from 'icons'
import { FC, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { formatNumberToCurrency } from 'utils'

const StyledContainer = styled.div`
  .highcharts-xaxis-grid,
  .highcharts-yaxis-grid {
    > path {
      &:nth-last-child(-n + 2) {
        stroke-width: 0;
      }
    }
  }
  .highcharts-axis-line {
    stroke-width: 0;
  }
`

export type HeatMapComponentProps = {
  dataSource?: Record<string, Record<string, number>>
}
export const HeatMapComponent: FC<HeatMapComponentProps> = (props) => {
  const { dataSource } = props
  const refContainer = useRef(null)
  const { xCategories, yCategories, seriesData } = useHeatmapChart(
    dataSource || {}
  )

  useEffect(() => {
    if (refContainer.current) {
      Heatmap(Highcharts)
      Accessibility(Highcharts)
      Highcharts.chart(refContainer.current, {
        chart: { type: 'heatmap' },
        plotOptions: { type: 'heatmap' },
        title: { text: '' },
        credits: { enabled: false },

        legend: {
          margin: 0,
          align: 'right',
          layout: 'vertical',
          verticalAlign: 'top',
          symbolWidth: 16,
          symbolHeight: 347
        },

        colorAxis: {
          y: 0,
          minColor: theme.colors.blue50,
          maxColor: theme.colors.blue500
        },

        xAxis: { categories: xCategories },

        yAxis: { categories: yCategories, title: null, reversed: true },

        tooltip: {
          headerFormat: '',
          pointFormatter() {
            let formatString = `<b>${formatNumberToCurrency(
              (this as any)?.value || 0
            )}</b> at `
            formatString += `<b>${
              (this as any)?.series?.yAxis?.categories[(this as any)?.y]
            }</b> `
            formatString += `<b>${
              (this as any)?.series?.xAxis?.categories[(this as any)?.x]
            }</b>`

            return formatString
          }
        },

        series: [
          {
            name: '',
            borderWidth: 2,
            borderColor: '#fff',
            data: seriesData,
            dataLabels: { enabled: false }
          }
        ]
      } as any)
    }
  }, [seriesData, xCategories, yCategories])

  if (!Object.keys(dataSource || {})?.length) {
    return (
      <Empty
        image={<Icon component={NoDataFolder} className="text-[80px]" />}
        imageStyle={{ height: 'auto' }}
        description="No data"
        className="min-h-[450px] flex flex-col items-center justify-center"
      />
    )
  }

  return <StyledContainer ref={refContainer} />
}

const useHeatmapChart = (
  dataSource: Record<string, Record<string, number>>
) => {
  return useMemo(() => {
    const xCategories: string[] = []
    const yCategories: string[] = []
    const seriesData: [number, number, number][] = []
    Object.keys(dataSource).forEach((xKey, xIndex) => {
      const value = dataSource[xKey as keyof typeof dataSource]
      xCategories.push(xKey)
      Object.keys(value).forEach((yKey, yIndex) => {
        if (xIndex === 0 || !yCategories.includes(yKey)) {
          yCategories.push(yKey)
        }

        seriesData.push(
          [xIndex, yIndex, value[yKey as keyof typeof value]] || 0
        )
      })
    })
    return {
      xCategories,
      yCategories,
      seriesData
    }
  }, [dataSource])
}
